import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelL } from '@sorare/core/src/atoms/typography';
import { Diamond } from '@sorare/core/src/components/rivals/branded/Diamond';
import { skeletonStyle } from '@sorare/core/src/style/utils';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import { GroupAvatar } from 'components/rivalsUserGroup/GroupAvatar';
import { MembersRecap } from 'components/rivalsUserGroup/MembersRecap';

import { LayeredGroupOverview_rivalsUserGroup } from './__generated__/index.graphql';

const Wrapper = styled.div`
  position: relative;
  margin-top: var(--quadruple-unit);
`;

const InnerWrapper = styled(Vertical)`
  border-radius: var(--double-unit);
  background: var(--c-neutral-300);
  min-height: 200px;
  justify-content: flex-start;
  &.loading {
    ${skeletonStyle}
    display: flex;
  }
  overflow: hidden;
`;

const StyledGridBackground = styled(GridBackground)`
  padding: calc(8 * var(--unit)) 0 var(--double-unit) 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--double-unit);
`;

const AvatarWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--quadruple-unit));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1;
`;

type Props = {
  userGroup: Nullable<LayeredGroupOverview_rivalsUserGroup>;
  loading?: boolean;
};

export const LayeredGroupOverview = ({
  userGroup,
  loading,
  children,
}: PropsWithChildren<Props>) => {
  return (
    <Wrapper>
      <InnerWrapper center className={classNames({ loading })}>
        <StyledGridBackground
          bgColor="var(--c-neutral-300)"
          height="170px"
          color={userGroup ? '#ad00ff' : null}
        >
          <Vertical gap={1} center>
            <HeadlineL>{userGroup?.displayName ?? '-'}</HeadlineL>
            {!!userGroup && (
              <LabelL color="var(--c-neutral-700)">
                <FormattedMessage
                  id="LayeredGroupOverview.membersCount"
                  defaultMessage="{count, plural, one {# member} other {# members}}"
                  values={{ count: userGroup.membershipsCount }}
                />
              </LabelL>
            )}
          </Vertical>
          {userGroup ? (
            <MembersRecap
              userGroup={userGroup}
              bgColor="var(--c-neutral-300)"
            />
          ) : (
            <LabelL color="var(--c-neutral-700)">
              <FormattedMessage
                id="LayeredGroupOverview.members"
                defaultMessage="Members"
              />
            </LabelL>
          )}
          {children}
        </StyledGridBackground>
      </InnerWrapper>
      <AvatarWrapper>
        {userGroup ? (
          <GroupAvatar userGroup={userGroup} />
        ) : (
          <Diamond
            width={80}
            height={80}
            noFilter
            color="var(--c-rivals-purple-600)"
          />
        )}
      </AvatarWrapper>
    </Wrapper>
  );
};

LayeredGroupOverview.fragments = {
  rivalsUserGroup: gql`
    fragment LayeredGroupOverview_rivalsUserGroup on RivalsUserGroup {
      slug
      displayName
      membershipsCount
      ...GroupAvatar_rivalsUserGroup
      ...MembersRecap_rivalsUserGroup
    }
    ${MembersRecap.fragments.rivalsUserGroup}
    ${GroupAvatar.fragments.rivalsUserGroup}
  ` as TypedDocumentNode<LayeredGroupOverview_rivalsUserGroup>,
};
